/**
 * LegWatch — the calm top of a leg's options. The "TripMuscle is watching" line
 * is the one always-visible element. Offers stay out of the way: when one has a
 * book-by within two weeks the banner opens with that single offer expanded,
 * otherwise everything folds into a link on the watching line ("2 offers for
 * this trip") that opens the banner in place. Closing the banner brings the link
 * back, so an offer is never lost.
 */
import { useState } from "react";
import type { PlanLeg } from "@/lib/types";
import LegOffers from "./LegOffers";
import WatchingLine from "./WatchingLine";
import { hasUrgentOffer, offersLinkText, orderOffers, todayIso } from "./fareLadderView";

interface LegWatchProps {
  leg: Pick<PlanLeg, "priceChoice" | "offers" | "focus">;
  /** Today as YYYY-MM-DD; injected for tests. */
  today?: string;
  /** Start with the banner open (tests, screenshots). */
  defaultOpen?: boolean;
}

export default function LegWatch({ leg, today = todayIso(), defaultOpen }: LegWatchProps) {
  const offers = orderOffers(leg.offers ?? [], today);
  const [open, setOpen] = useState<boolean>(defaultOpen ?? hasUrgentOffer(offers, today));
  const showLink = offers.length > 0 && !open;
  return (
    <div className="space-y-2">
      <WatchingLine leg={leg} today={today}>
        {showLink && (
          <>
            {" · "}
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-expanded={false}
              className="font-medium text-[hsl(var(--primary))] underline-offset-2 hover:underline"
            >
              {offersLinkText(offers.length)}
            </button>
          </>
        )}
      </WatchingLine>
      {open && offers.length > 0 && <LegOffers offers={offers} onClose={() => setOpen(false)} />}
    </div>
  );
}
